<script setup lang="ts">
import { onMounted, reactive, ref, watch, computed } from "vue";
import { ElNotification } from "element-plus";
import { Close } from "@element-plus/icons-vue";
import Helper from "~/composable/helper";
import { useProductStore } from "~/stores/productStore";
import { useCustomerProfileStore } from "~/stores/customerProfileStore";
import { useAuthStore } from "~/stores/auth";
import { Profile } from "~/types/profile";
import { FormRuleType } from "~/types/element-ui/FormRuleType";
import { ElementUIFormRef } from "~/types/element-ui/ElementUIFormRef";
import { ProductSource } from "~/types/enums/ProductSource";
import { useOrdersStore } from "~/projects/order-manager/stores/ordersStore";
import { DeliveryPartnerType } from "~/projects/order-manager/types/enums/DeliveryPartnerType";
import {
  DeliveryStatus,
  DeliveryStatusLabels,
} from "~/projects/order-manager/types/enums/DeliveryStatus";
import { useDebouncedSearch } from "~/composable/useDebouncedSearch";
import { useConfigStore } from "~/stores/configStore";
import { OrderMessageLabels } from "~/types/helpDeskSettings";
import { PermissionNames } from "~/configs/permission_names";
import { Product } from "~/types/product";

const ordersStore = useOrdersStore();
const productStore = useProductStore();
const profileStore = useCustomerProfileStore();
const configStore = useConfigStore();
const { getDeliveryIntegrationIcon } = Helper();

// Wording of the order confirmation message. Clients localise or reword these
// from Help Desk settings; the backend always ships a complete set through the
// settings cache, but keep literals here so an older cached config still works.
const ORDER_MESSAGE_LABEL_FALLBACKS: OrderMessageLabels = {
  name: "Name",
  phone: "Mobile No #",
  address: "Address#",
  color: "Color",
  size: "Size",
  sku: "SKU",
  quantity: "Quantity",
  price: "Price",
  sub_total: "Sub Total",
  delivery_charge: "Delivery charge",
  discount: "Discount",
  advance: "Advance",
  grand_total: "Amount To Be Paid",
  footer: "Kindly confirm the order.",
  separator: "-----------------------------------------------------",
};

const orderMessageLabels = computed<OrderMessageLabels>(() => ({
  ...ORDER_MESSAGE_LABEL_FALLBACKS,
  ...(configStore.globalConfig.help_desk?.order_message_labels ?? {}),
}));

// Discount type enums for backend API
const DiscountTypeEnum = {
  FIXED: 1,
  PERCENTAGE: 2,
} as const;

type ProductDiscountType = 1 | 2 | null;
const props = defineProps({
  showHeader: { type: Boolean, default: true },
  showTemplates: { type: Boolean, default: false },
  // Order source (source_type/source_id) is read from ordersStore.orderableData,
  // which each open path sets before mounting the form.
  isEditMode: { type: Boolean, default: false },
  orderToEdit: { type: Object, default: null },
  // Standalone create (Orders page): render a profile search-select and a
  // required status select, and persist via the store() endpoint.
  showProfileSelect: { type: Boolean, default: false },
  showStatusSelect: { type: Boolean, default: false },
});

const emit = defineEmits(["close-modal", "set-ordermessage"]);

const formRef = ref<ElementUIFormRef | null>(null);
const formSubmitting = ref(false);

/*
 * The reason the last save attempt failed, shown in the form itself rather than
 * as a notification that fades. The form now stays on screen when a save is
 * refused, so the message has to stay with it: the agent has to read why, fix
 * the one field at fault and send it again.
 */
const saveError = ref("");

const originalProfile = ref<any | null>(null);

// formData includes selected profile + custom fields
const formData = reactive<
  Partial<Profile> & {
    address: {
      country?: string;
      state?: string;
      zip_code?: string;
      street?: string;
    };
    products: {
      product_id?: number;
      quantity?: number;
      price?: number;
      unit_price?: number;
      discount_type?: 1 | 2 | null;
      discount_amount?: number;
    }[];
    deliveryCharge?: string;
    discount?: string;
    advance?: string;
    sub_total?: string;
    total?: string;
    delivery_partner_integration_id?: number;
    email?: string;
    status?: number;
  }
>({
  address: {},
  products: [],
});

// Profile search-select model (standalone create only).
const selectedProfileId = ref<number | null>(null);

const finalMessage = ref("");

// Fraud checker UI state
const fraudInfoLoading = ref(false);
const showPhoneLengthInfo = ref(false);
const fetchFraudInfo = async (phone: string) => {
  if (!phone) return;
  try {
    fraudInfoLoading.value = true;
    // calls the store action which will populate _fraudCheckerData
    await profileStore.getFraudCheckerTableData(phone);
  } catch (e) {
    console.error("Error fetching fraud info", e);
  } finally {
    fraudInfoLoading.value = false;
  }
};

// Delivery integrations state
const deliveryIntegrations = ref<Array<any>>([]);
const selectedDeliveryIntegration = ref<any>(null);
const selectedDeliveryIntegrationPartner = computed(() => {
  if (selectedDeliveryIntegration.value) {
    const [partnerType] = selectedDeliveryIntegration.value.split(":");
    return partnerType;
  }
  return null;
});

// When the org has no delivery integrations, the integration select is hidden
// and both the integration and the address become optional.
const hasDeliveryIntegrations = computed(
  () => deliveryIntegrations.value.length > 0,
);

// Populate the form fields from a selected/loaded profile. Shared by create-mode
// mount and the profile search-select.
const populateFromProfile = async (profile: any) => {
  if (!profile) return;
  originalProfile.value = { ...profile };
  formData.name = profile.name;
  formData.phone = profile.default_phone?.phone || "";
  formData.email =
    profile.emails?.find((e: any) => e.is_default)?.email ||
    profile.emails?.[0]?.email ||
    "";
  if (profile.default_phone) {
    await fetchFraudInfo(profile.default_phone.phone);
  }
  const defaultAddress =
    profile.addresses?.find((a: any) => a.is_default) || profile.addresses?.[0];
  if (defaultAddress) {
    formData.address = {
      country: "Bangladesh",
      state: defaultAddress.state || "",
      zip_code: defaultAddress.zip_code || "",
      street: defaultAddress.street || "",
    };
  }
};

// Profile search-select (standalone create). Mirrors the CRM lead form.
const { remoteMethod, loading } = useDebouncedSearch((query: string) => {
  profileStore._simpleFilter.search = query;
  profileStore.getSimpleProfiles();
});

const onProfileSelected = async (profileId: number | null) => {
  if (!profileId) {
    originalProfile.value = null;
    return;
  }
  profileStore._fraudCheckerData = null;
  const profile = await profileStore.getProfile(profileId);
  await populateFromProfile(profile);
};

// A profile created inline via the "+" button: make it selectable, select it,
// and populate the form from it.
const onProfileCreated = async (created: any) => {
  if (!created?.id) return;
  const profile = (await profileStore.getProfile(created.id)) || created;
  if (!profileStore.simpleProfiles.some((p: any) => p.id === profile.id)) {
    profileStore.simpleProfiles.unshift(profile);
  }
  selectedProfileId.value = profile.id;
  await populateFromProfile(profile);
};

// In the standalone create form the recipient fields only appear once a profile
// is chosen — selected from the search or freshly created via the "+" button.
const showRecipientFields = computed(
  () => !props.showProfileSelect || !!originalProfile.value?.id,
);

// Delivery charge is only required when an integration will handle delivery.
const deliveryChargeRules = computed(() => {
  const amount = {
    pattern: /^[0-9]+(\.[0-9]{1,2})?$/,
    message: "Enter a valid amount",
    trigger: "blur",
  };
  return hasDeliveryIntegrations.value
    ? [
        {
          required: true,
          message: "Delivery charge is required",
          trigger: "blur",
        },
        amount,
      ]
    : [amount];
});
const pathaoStores = ref<any[]>([]);
const steadfastStores = ref<any[]>([]);
const karbariStores = ref<any[]>([]);
const ecomdriveStores = ref<any[]>([]);
const nuportStores = ref<any[]>([]);
const bizmotionStores = ref<any[]>([]);
const loadingIntegrations = ref(false);

// Pathao stores dropdown state
const pathaoStoresList = ref<any[]>([]);
const selectedPathaoStore = ref<any>(null);
const loadingPathaoStores = ref(false);

// Fields specific to Pathao / Steadfast forms (mirrors their components)
const pathaoFields = reactive({
  recipient_city: "",
  recipient_zone: "",
  recipient_area: "",
  delivery_type: 0,
  item_type: 2,
  item_quantity: 1,
  item_weight: 0.5,
  item_description: "",
  amount_to_collect: 0,
});

const steadfastFields = reactive({
  recipient_name: "",
  recipient_phone: "",
  recipient_address: "",
  cod_amount: 0,
  note: "",
  item_weight: 0.5,
  item_description: "",
  total_lot: 1,
  delivery_type: 0,
});

// Karbari-specific fields
const karbariMetadata = computed(() => {
  if (
    selectedDeliveryIntegrationPartner.value === "karbari" &&
    selectedDeliveryIntegration.value
  ) {
    const integration = karbariStores.value.find(
      (store) => store.id === formData.delivery_partner_integration_id,
    );
    return integration?.meta_data || null;
  }
  return null;
});
const karbariSmartcommSource = computed(() => {
  return karbariMetadata.value?.sources.find(
    (source: any) => source.name === "Smartcomm",
  );
});
const karbariFields = reactive({
  karbari_brand_id: null as number | null,
  karbari_source_id:
    karbariSmartcommSource.value?.karbari_source_id || (null as number | null),
  karbari_courier_id: null as number | null,
  delivery_zone: 1, // Default to Inside Dhaka/CTG
  delivery_type: 0, // Default to Regular
  karbari_bank_id: null as number | null,
});

// Nuport-specific fields
const nuportFields = reactive({
  // Optional shipping date (no default). When set it is sent to Nuport as orderDate.
  shippingDate: null as string | null,
  paymentMethod: "Cash" as string,
  nuport_courier_id: null as string | null,
  nuport_source_id: null as string | number | null,
  // Optional warehouse/pickup location; Nuport falls back to its latest warehouse.
  nuport_pickup_location_id: null as string | null,
  // Per-order approval: true = Approval (auto-approve), false = Pending.
  // Defaults from the selected integration; user can override per order.
  auto_approve: true,
  // When ticked, a 0 delivery charge is sent to Nuport (free delivery).
  // When unticked, the delivery charge entered on the order is sent to Nuport.
  free_delivery: false,
});

// BizMotion-specific fields
const bizmotionFields = reactive({
  // The status the order is filed as. Empty files it as Pending, which needs no
  // permission on the BizMotion side. Defaults from the selected integration.
  bizmotion_status: "" as string,
  bizmotion_source: "" as string,
  // BizMotion guesses a district from the street address when none is sent, so
  // all three are optional. City and area only apply to accounts that record the
  // delivery address that far down.
  bizmotion_district: "" as string,
  bizmotion_city: "" as string,
  bizmotion_area: "" as string,
  // How an advance was collected. Only sent when there is an advance to record.
  paymentMethod: "Cash on Delivery" as string,
  // When ticked, a 0 shipping charge is sent to BizMotion (free delivery).
  free_delivery: false,
  // When ticked, BizMotion refuses a repeat order instead of creating a second
  // one flagged as a duplicate.
  reject_duplicate: false,
});

// Karbari metadata computed properties

const karbariBrands = computed(() => {
  return karbariMetadata.value?.brands || [];
});

const karbariSources = computed(() => {
  return karbariMetadata.value?.sources || [];
});

const karbariCouriers = computed(() => {
  return karbariMetadata.value?.couriers || [];
});

const karbariBankAccounts = computed(() => {
  return karbariMetadata.value?.bank_accounts || [];
});

// Check if EcomDrive is selected
const isEcomDriveSelected = computed(() => {
  return selectedDeliveryIntegrationPartner.value === "ecomdrive";
});

// Check if Nuport is selected
const isNuportSelected = computed(() => {
  return selectedDeliveryIntegrationPartner.value === "nuport";
});

// Nuport syncs a regular price and an optional sale price. Only Nuport products
// carry a sale price, so this returns one just for them.
const productSalePrice = (product?: Product | null): number | null => {
  if (!isNuportSelected.value) return null;
  const salePrice = Number(product?.sale_price ?? 0);
  return salePrice > 0 ? salePrice : null;
};

// The price a product enters the order at: its sale price when Nuport has one,
// otherwise the regular price. This is what gets sent back to the partner as the
// line item's unit price.
const effectiveUnitPrice = (product?: Product | null): number => {
  return productSalePrice(product) ?? product?.price ?? 0;
};

// Nuport metadata computed properties
const nuportMetadata = computed(() => {
  if (
    selectedDeliveryIntegrationPartner.value === "nuport" &&
    selectedDeliveryIntegration.value
  ) {
    const integration = nuportStores.value.find(
      (store) => store.id === formData.delivery_partner_integration_id,
    );
    return integration?.meta_data || null;
  }
  return null;
});

const nuportCouriers = computed(() => {
  return nuportMetadata.value?.delivery_partners || [];
});

const nuportOrderSources = computed(() => {
  return nuportMetadata.value?.order_sources || [];
});

const nuportPickupLocations = computed(() => {
  return nuportMetadata.value?.pickup_locations || [];
});

const getNuportPickupLocationLabel = (location: any): string => {
  return (
    location?.label ||
    location?.internalName ||
    location?.address ||
    location?.id ||
    "Unnamed Warehouse"
  );
};

const getNuportOrderSourceLabel = (source: any): string => {
  if (typeof source === "string") return source;
  return (
    source?.name ||
    source?.source_name ||
    source?.label ||
    source?.value ||
    source?.id ||
    "Unknown Source"
  );
};

const getNuportOrderSourceValue = (source: any): string | number => {
  if (typeof source === "string") return source;
  return source?.id ?? source?.value ?? source?.name ?? source?.source_name;
};

// BizMotion metadata computed properties
const bizmotionMetadata = computed(() => {
  if (
    selectedDeliveryIntegrationPartner.value === "bizmotion" &&
    selectedDeliveryIntegration.value
  ) {
    const integration = bizmotionStores.value.find(
      (store) => store.id === formData.delivery_partner_integration_id,
    );
    return integration?.meta_data || null;
  }
  return null;
});

// The statuses a NEW order may be filed as. Deliberately not the same list as
// `statuses` — BizMotion grants "file an order as X" and "move an order to X"
// separately, and only the former applies when creating one.
const bizmotionCreateStatuses = computed<string[]>(() => {
  return bizmotionMetadata.value?.create_statuses || [];
});

const bizmotionSources = computed<string[]>(() => {
  return bizmotionMetadata.value?.sources || [];
});

// The source an order is filed under when none is sent, as reported by
// BizMotion. Shown as the placeholder so the fallback isn't a surprise.
const bizmotionDefaultSource = computed<string>(() => {
  return bizmotionMetadata.value?.default_source || "Public API";
});

// BizMotion refuses /orders/create outright unless the account has ticked
// "Allow the API to create orders", so surface that before the agent fills in
// a whole order that cannot be submitted.
const bizmotionCanCreateOrder = computed<boolean>(() => {
  const permissions = bizmotionMetadata.value?.permissions;
  // Treat an un-synced integration as capable: the warning is for a known-off
  // permission, not for metadata we haven't fetched yet.
  return permissions ? permissions.can_create_order !== false : true;
});

const filteredProducts = computed(() => {
  const allProducts = productStore.allProducts || [];
  const partner = selectedDeliveryIntegrationPartner.value;

  if (partner === "karbari") {
    return allProducts.filter((p) => p.source === ProductSource.Karbari);
  }

  if (partner === "ecomdrive") {
    return allProducts.filter((p) => p.source === ProductSource.EcomDrive);
  }

  if (partner === "nuport") {
    return allProducts.filter((p) => p.source === ProductSource.Nuport);
  }

  if (partner === "bizmotion") {
    return allProducts.filter((p) => p.source === ProductSource.BizMotion);
  }

  /*
   * Pathao, Steadfast, or no courier yet.
   *
   * Shopify products belong here as well as local ones. The shop keeps their
   * count, so SmartComm asks it to take the units off before the order is
   * created — an order typed in here is invisible to the shop otherwise, and
   * its website goes on offering something already promised away.
   *
   * The other partners above stay narrow on purpose: an ERP order has to be
   * filed against that ERP's own catalogue.
   */
  return allProducts.filter(
    (p) =>
      p.source === ProductSource.Local ||
      p.source === undefined ||
      p.source === ProductSource.Shopify,
  );
});

/* A product whose count belongs to a shop, not to us. */
const isStoreProduct = (product?: Product | null) =>
  !!product && product.source === ProductSource.Shopify;

/*
 * What the SHOP has, at the place an order would take it from.
 *
 * Not products.quantity, which is our copy of the total across every place.
 * Those differ — 8 here can be 7 at the shop and 1 in a back room — and the
 * order is checked against the one place. Showing the bigger number is how an
 * agent promises what is not there.
 */
const storeStock = (product?: Product | null): number | null =>
  product?.store_quantity ?? null;

// 👇 This ref will be updated by the filter-method
const displayedProducts = ref([...filteredProducts.value]);

watch(filteredProducts, (newList) => {
  displayedProducts.value = [...newList];
});

const handleProductFilter = (query: string) => {
  if (!query) {
    displayedProducts.value = [...filteredProducts.value];
    return;
  }

  const searchText = query.toLowerCase();
  displayedProducts.value = filteredProducts.value.filter((p) => {
    return (
      p.name?.toLowerCase().includes(searchText) ||
      p.size?.toLowerCase().includes(searchText) ||
      p.sku?.toLowerCase().includes(searchText) ||
      p.color?.toLowerCase().includes(searchText)
    );
  });
};

// Custom product selector state
const productSearchQuery = ref("");
const productColorQuery = ref("");
const productSizeQuery = ref("");

// Products created from this form, newest first. They are pinned to the top of
// the selector so the one just created is the first thing to hand rather than
// something to go hunting for in the full catalogue.
const justCreatedProductIds = ref<number[]>([]);

// Karbari / EcomDrive / Nuport / BizMotion orders draw their products from that
// partner's own catalogue, so a locally created product would never appear in
// the list. Only offer the button when the selector is showing local products.
const canCreateProduct = computed(
  () =>
    !["karbari", "ecomdrive", "nuport", "bizmotion"].includes(
      selectedDeliveryIntegrationPartner.value ?? "",
    ) && isUserPermitted(PermissionNames.PRODUCT_CREATE),
);

const onProductCreated = (product: Product) => {
  if (!product?.id) return;
  justCreatedProductIds.value = [
    product.id,
    ...justCreatedProductIds.value.filter((id) => id !== product.id),
  ];
  // Clear the searches, otherwise the new product is pinned to the top of a
  // list that an unrelated search term is still filtering it out of.
  productSearchQuery.value = "";
  productColorQuery.value = "";
  productSizeQuery.value = "";
};

// Track selected product IDs
const selectedProductIds = computed(() => {
  return formData.products
    .map((p) => p.product_id)
    .filter((id): id is number => id !== undefined);
});

// Check if a product is already selected
const isProductSelected = (productId: number) => {
  return selectedProductIds.value.includes(productId);
};

/**
 * Whether this product has run out.
 *
 * Only meaningful for LOCAL products. Karbari, Nuport and WooCommerce sync every
 * product with a hard-coded quantity of 0 and nothing ever decrements them, so
 * reading their count as stock would make their whole catalogue unorderable.
 * The backend applies the same qualifier when it reserves.
 */
const isOutOfStock = (product?: Product | null) => {
  if (!product) return false;

  // A store product is judged on the shop's own figure at the place the units
  // come from. Our copy of the total would let an agent pick something the
  // shop cannot actually spare.
  if (isStoreProduct(product)) {
    const atShop = storeStock(product);
    return atShop !== null && atShop <= 0;
  }

  // `source === undefined` counts as Local, the same way localProducts reads it.
  return (
    (product.source === ProductSource.Local || product.source === undefined) &&
    (product.quantity ?? 0) <= 0
  );
};

const isProductDisabled = (product: Product) =>
  isProductSelected(product.id) || isOutOfStock(product);

const selectProduct = (productId: number) => {
  const candidate = productStore.allProducts.find((p) => p.id === productId);

  if (isOutOfStock(candidate)) {
    ElNotification.warning({
      title: "Out of Stock",
      message: `${candidate?.name ?? "This product"} has no stock left`,
    });
    return;
  }

  // Check if product is already selected
  if (isProductSelected(productId)) {
    ElNotification.warning({
      title: "Already Selected",
      message: "This product is already selected",
    });
    return;
  }

  // Add new product row directly
  const product = productStore.allProducts.find((p) => p.id === productId);
  const unitPrice = effectiveUnitPrice(product);
  formData.products.push({
    product_id: productId,
    quantity: 1,
    unit_price: unitPrice,
    price: unitPrice,
    discount_type: 1, // Default to fixed
    discount_amount: 0,
  });
};

const removeSelectedProduct = (index: number) => {
  // Remove the product row completely
  formData.products.splice(index, 1);
};

const filteredProductsForSelector = computed(() => {
  let products = filteredProducts.value;

  if (productSearchQuery.value) {
    const searchText = productSearchQuery.value.toLowerCase();
    products = products.filter(
      (p) =>
        p.sku?.toLowerCase().includes(searchText) ||
        p.name?.toLowerCase().includes(searchText) ||
        false,
    );
  }

  if (productColorQuery.value) {
    const searchText = productColorQuery.value.toLowerCase();
    products = products.filter(
      (p) => p.color?.toLowerCase().includes(searchText) || false,
    );
  }

  if (productSizeQuery.value) {
    const searchText = productSizeQuery.value.toLowerCase();
    products = products.filter(
      (p) => p.size?.toLowerCase().includes(searchText) || false,
    );
  }

  if (justCreatedProductIds.value.length) {
    const rank = new Map(
      justCreatedProductIds.value.map((id, index) => [id, index]),
    );
    const pinned = justCreatedProductIds.value
      .map((id) => products.find((p) => p.id === id))
      .filter((p): p is (typeof products)[number] => !!p);

    products = [...pinned, ...products.filter((p) => !rank.has(p.id))];
  }

  return products;
});

// The base product a variant belongs to, whichever partner catalogue it came
// from. A product carries at most one of these, since it has a single source.
const parentProductOf = (
  product: Product,
): { id: string | null; name: string | null } => {
  if (product.nuport_parent_product_id) {
    return {
      id: `nuport:${product.nuport_parent_product_id}`,
      name: product.nuport_parent_product_name ?? null,
    };
  }

  if (product.bizmotion_parent_product_id) {
    return {
      id: `bizmotion:${product.bizmotion_parent_product_id}`,
      name: product.bizmotion_parent_product_name ?? null,
    };
  }

  return { id: null, name: null };
};

// Group the (already filtered/searched) products by their partner parent product
// so variants of the same base product appear together. Products without a
// parent id (e.g. local products) become their own single-item group.
const groupedProductsForSelector = computed(() => {
  type SelectorProduct = (typeof filteredProductsForSelector.value)[number];
  const groups: {
    key: string;
    name: string;
    isGroup: boolean;
    products: SelectorProduct[];
  }[] = [];
  const byKey = new Map<string, (typeof groups)[number]>();

  for (const p of filteredProductsForSelector.value) {
    const parent = parentProductOf(p);
    const key = parent.id ? `parent:${parent.id}` : `single:${p.id}`;
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        name: parent.id ? parent.name || p.name : p.name,
        isGroup: !!parent.id,
        products: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }
    group.products.push(p);
  }

  return groups;
});

const getSelectedProduct = (productId: number | undefined) => {
  if (!productId) return null;
  return productStore.allProducts.find((p) => p.id === productId);
};

const rules = reactive<FormRuleType>({
  name: [
    { required: true, message: "Name is required", trigger: "blur" },
    {
      min: 3,
      max: 64,
      message: "Name must be between 3 and 64 characters",
      trigger: "blur",
    },
  ],
  phone: [
    { required: true, message: "Phone is required", trigger: "blur" },
    {
      pattern: /^[0-9+\-()\s]{7,15}$/,
      message: "Enter a valid phone number",
      trigger: "blur",
    },
  ],
  address: [
    { required: true, message: "Address is required", trigger: "blur" },
    {
      min: 10,
      message: "Address must be at least 10 characters",
      trigger: "blur",
    },
  ],
  product_id: [
    { required: true, message: "Product is required", trigger: "change" },
  ],
  total: [
    { required: true, message: "Total is required", trigger: "blur" },
    {
      pattern: /^[0-9]+(\.[0-9]{1,2})?$/,
      message: "Enter a valid amount",
      trigger: "blur",
    },
  ],
});

const sendOrderConfirmation = async (isDraft = false) => {
  // Set immediately, before any await: the ref is written synchronously, so a
  // second click landing in the same frame reads `true` and turns back here.
  if (formSubmitting.value) return;
  formSubmitting.value = true;
  saveError.value = "";

  // Every other bail-out below clears the flag; this one did not, which left the
  // button stuck on "Processing" with no way back short of reopening the dialog.
  if (!formRef.value) {
    formSubmitting.value = false;
    return;
  }

  // Delivery integration and address are only required when the org actually
  // has integrations configured.
  if (hasDeliveryIntegrations.value && !selectedDeliveryIntegration.value) {
    ElNotification.error("Please select a delivery integration");
    formSubmitting.value = false;
    return;
  }
  if (
    hasDeliveryIntegrations.value &&
    (formData?.address?.street?.length ?? 0) < 10
  ) {
    ElNotification.error("Please enter an address with at least 10 characters");
    formSubmitting.value = false;
    return;
  }
  if (props.showProfileSelect && !originalProfile.value?.id) {
    ElNotification.error("Please select a customer profile");
    formSubmitting.value = false;
    return;
  }
  if (props.showStatusSelect && !formData.status) {
    ElNotification.error("Please select a status");
    formSubmitting.value = false;
    return;
  }
  if (formData.phone.length < 11) {
    ElNotification.error(
      "Please enter a valid phone number with at least 11 digits",
    );
    formSubmitting.value = false;
    return;
  }
  if (formData.phone.length > 11) {
    ElNotification.error(
      "Please enter a valid phone number with at most 11 digits",
    );
    formSubmitting.value = false;
    return;
  }

  // Validate Nuport-specific required fields
  const partner = selectedDeliveryIntegrationPartner.value;
  if (partner === "nuport") {
    if (!nuportFields.nuport_courier_id) {
      ElNotification.error("Please select a courier for Nuport");
      formSubmitting.value = false;
      return;
    }
    if (!nuportFields.paymentMethod) {
      ElNotification.error("Please select a payment method for Nuport");
      formSubmitting.value = false;
      return;
    }
    if (!nuportFields.nuport_source_id) {
      ElNotification.error("Please select an order source for Nuport");
      formSubmitting.value = false;
      return;
    }
  }

  // Validate BizMotion-specific required fields
  if (partner === "bizmotion") {
    // The account has to grant order creation before anything can be filed.
    if (!bizmotionCanCreateOrder.value) {
      ElNotification.error(
        'BizMotion has not allowed this account to create orders. Tick "Allow the API to create orders" on Settings → API, then re-sync the integration.',
      );
      formSubmitting.value = false;
      return;
    }
    // An advance is recorded against a payment method, so it needs one.
    const advanceAmount = parseFloat(formData.advance || "0");
    if (advanceAmount > 0 && !bizmotionFields.paymentMethod) {
      ElNotification.error(
        "Please select how the advance payment was collected",
      );
      formSubmitting.value = false;
      return;
    }
    // A status that isn't on the account's create list is refused with 422, so
    // catch it here rather than after a round trip.
    if (
      bizmotionFields.bizmotion_status &&
      bizmotionCreateStatuses.value.length &&
      !bizmotionCreateStatuses.value.includes(bizmotionFields.bizmotion_status)
    ) {
      ElNotification.error(
        `"${bizmotionFields.bizmotion_status}" is not a status BizMotion allows new orders to be created in`,
      );
      formSubmitting.value = false;
      return;
    }
  }

  await formRef.value.validate(async (valid: boolean) => {
    if (!valid) {
      formSubmitting.value = false;
      return;
    }

    if (
      !formData.products.length ||
      formData.products.some((p) => !p.product_id)
    ) {
      ElNotification.error("Please select at least one product.");
      formSubmitting.value = false;
      return;
    }

    // Validate partner-specific requirements BEFORE profile updates
    const partner = selectedDeliveryIntegrationPartner.value;

    if (partner === "pathao" && !selectedPathaoStore.value) {
      ElNotification.error("Please select a Pathao store");
      formSubmitting.value = false;
      return;
    }

    if (partner === "karbari") {
      if (!karbariFields.karbari_brand_id) {
        ElNotification.error("Please select a brand");
        formSubmitting.value = false;
        return;
      }
      if (!karbariFields.karbari_source_id) {
        ElNotification.error("Please select a source");
        formSubmitting.value = false;
        return;
      }
      if (!karbariFields.karbari_courier_id) {
        ElNotification.error("Please select a courier");
        formSubmitting.value = false;
        return;
      }
      const advanceAmount = parseFloat(formData.advance || "0");
      if (advanceAmount > 0 && !karbariFields.karbari_bank_id) {
        ElNotification.warning(
          "Bank account details are required for advance payments",
        );
        formSubmitting.value = false;
        return;
      }
    }

    const profileId = originalProfile.value?.id;
    const originalAddress = (originalProfile.value as any)?.addresses?.[0];

    const nameChanged =
      formData.name && formData.name !== originalProfile.value?.name;

    const phone = formData.phone?.trim();
    const existingPhones = (originalProfile.value as any)?.phones
      ? [...(originalProfile.value as any).phones]
      : [];
    const isPhonePresent = phone
      ? existingPhones.some((p: any) => p.phone === phone)
      : true;

    const addressChanged =
      !originalAddress ||
      formData.address?.country !== originalAddress.country ||
      formData.address?.state !== originalAddress.state ||
      formData.address?.zip_code !== originalAddress.zip_code ||
      formData.address?.street !== originalAddress.street;

    let profileUpdated = false;

    // 1. Update Name
    if (nameChanged) {
      const updatedProfile = {
        ...originalProfile.value,
        name: formData.name,
      };

      const updated = await profileStore.updateProfile(updatedProfile);
      if (updated) {
        (originalProfile.value as any).name = formData.name;
        profileUpdated = true;
      } else {
        ElNotification.error("Failed to update profile name");
        formSubmitting.value = false;
        return;
      }
    }

    // 2. Add phone if missing (merge conflict handling is inside updateProfileRelations)
    if (phone && !isPhonePresent) {
      profileStore.setMergeRedirectMode("stay");
      const updatedProfile = await profileStore.updateProfileRelations({
        id: profileId,
        phones: [...existingPhones, { phone, is_default: false }],
      });

      if (!updatedProfile) {
        formSubmitting.value = false;
        return;
      }

      originalProfile.value = updatedProfile as any;
      profileUpdated = true;
    }

    // 2b. Add email if missing (mirrors the phone flow above)
    const email = formData.email?.trim();
    const existingEmails = (originalProfile.value as any)?.emails
      ? [...(originalProfile.value as any).emails]
      : [];
    const isEmailPresent = email
      ? existingEmails.some((e: any) => e.email === email)
      : true;

    if (email && !isEmailPresent) {
      profileStore.setMergeRedirectMode("stay");
      const updatedProfile = await profileStore.updateProfileRelations({
        id: profileId,
        emails: [...existingEmails, { email, is_default: false }],
      });

      if (!updatedProfile) {
        formSubmitting.value = false;
        return;
      }

      originalProfile.value = updatedProfile as any;
      profileUpdated = true;
    }

    // 3. Update or Create Address
    if (formData.address && addressChanged) {
      const payload = {
        country: formData.address.country || "",
        state: formData.address.state || "",
        zip_code: formData.address.zip_code || "",
        street: formData.address.street || "",
        is_default: true,
        address_line_1: formData.address.street || "",
      };
      console.log("originalAddress:", originalAddress);
      if (originalAddress) {
        const updatedAddress = await profileStore.updateAddress(
          profileId!,
          originalAddress.id,
          payload,
        );
        if (updatedAddress) {
          (originalProfile.value as any).addresses[0] = updatedAddress;
          profileUpdated = true;
        } else {
          ElNotification.error("Failed to update address");
          formSubmitting.value = false;
          return;
        }
      } else {
        const createdAddress = await profileStore.createAddress(
          profileId!,
          payload,
        );
        if (createdAddress) {
          (originalProfile.value as any).addresses = [createdAddress];
          profileUpdated = true;
        } else {
          ElNotification.error("Failed to create address");
          formSubmitting.value = false;
          return;
        }
      }
    }

    if (profileUpdated) {
      ElNotification.success("Profile updated successfully");
    }

    // 4. Compose order message

    const labels = orderMessageLabels.value;

    const productLinesFormatted = formData.products
      .map((item) => {
        const product = productStore.allProducts.find(
          (p) => p.id === item.product_id,
        );
        if (!product) return "";
        return `
${labels.separator}
${product.name}
${product.color ? `${labels.color}: ${product.color}` : ""} | ${
          product.size ? ` ${labels.size}: ${product.size}` : ""
        }
${labels.sku}: ${product.sku}
${labels.quantity}: ${item.quantity || 1}
${labels.price} ${item.price || 0}
`.trim();
      })
      .join("\n");

    const subtotal = calculateSubtotals();
    const delivery = parseFloat(formData.deliveryCharge || "0");
    const discount = parseFloat(formData.discount || "0");
    const advance = parseFloat(formData.advance || "0");
    const grandTotal = (subtotal + delivery - discount - advance).toFixed(2);

    const addressFormatted = `${formData.address.street || ""}, ${
      formData.address.state || ""
    }-${formData.address.zip_code || ""}, ${formData.address.country || ""}`;

    const messageBody = `
${labels.name}: ${formData.name}
${labels.phone} ${formData.phone}
${labels.address} ${addressFormatted}

${productLinesFormatted}
${labels.separator}
${labels.sub_total} = ${subtotal.toFixed(2)}
${labels.delivery_charge} = ${formData.deliveryCharge || "0"}
${discount > 0 ? `${labels.discount} = ${discount.toFixed(2)}` : ""}
${advance > 0 ? `${labels.advance} = ${advance.toFixed(2)}` : ""}
${labels.grand_total} = ${grandTotal}

${labels.footer}

`.trim();

    // Build products payload expected by backend
    // discount_type: 1 = fixed, 2 = percentage, null = no discount
    // discount_amount: original discount value from user input
    // total_discount_value: actual total discount deducted (considering quantity)
    // Formula: (unit_price - discount_per_unit) * quantity = final_price
    const productsPayload = formData.products.map((p) => {
      const unitPrice = p.unit_price || 0;
      const quantity = p.quantity || 1;
      const discountAmount = p.discount_amount || 0;
      const discountType: ProductDiscountType = p.discount_type;

      const totalDiscountValue = calculateTotalDiscountValue(
        unitPrice,
        quantity,
        discountAmount,
        discountType,
      );
      const finalPrice = calculateProductTotal(p);

      return {
        id: p.product_id,
        unit_price: unitPrice,
        quantity,
        discount_type: discountType, // Use directly - no conversion needed
        discount_amount: discountAmount,
        total_discount_value: parseFloat(totalDiscountValue.toFixed(2)),
        total: parseFloat(finalPrice.toFixed(2)),
      };
    });

    // Common base payload
    const basePayload: any = {
      recipient_name: formData.name,
      recipient_phone: formData.phone,
      recipient_address: `${formData.address?.street || ""}`,
      sub_total: formData.sub_total ? parseFloat(formData.sub_total) : 0,
      discount: formData.discount ? parseFloat(formData.discount) : 0,
      advance: formData.advance ? parseFloat(formData.advance) : 0,
      delivery_charge: formData.deliveryCharge
        ? parseFloat(formData.deliveryCharge)
        : 0,
      products: productsPayload,
      source_type: ordersStore.orderableData?.source_type ?? null,
      source_id: ordersStore.orderableData?.source_id ?? null,
      profile_id: originalProfile.value?.id,
      remarks: finalMessage.value,
      delivery_partner_integration_id: formData.delivery_partner_integration_id,
      item_weight: 0.5, // Default weight
    };
    // Standalone create supplies a starting status (Draft/Confirmed).
    if (props.showStatusSelect && formData.status) {
      basePayload.status = formData.status;
    }

    // Call respective delivery create/update API based on selectedDeliveryIntegration

    let returnResponse: any = null;

    // Standalone create persists via store() (createOrder); every other create
    // path uses createDraftOrder. Both share the reusable backend creation path.
    const createFn = props.showStatusSelect
      ? ordersStore.createOrder
      : ordersStore.createDraftOrder;

    // Helper function to remove delivery-partner-specific fields
    const cleanPayloadForPartnerChange = (
      payload: any,
      currentPartnerType: DeliveryPartnerType,
    ) => {
      const cleaned = { ...payload };

      // Remove Pathao-specific fields if not Pathao
      if (currentPartnerType !== DeliveryPartnerType.Pathao) {
        delete cleaned.store_id;
        delete cleaned.recipient_city;
        delete cleaned.recipient_zone;
        delete cleaned.recipient_area;
        delete cleaned.item_type;
      }

      // Remove Steadfast-specific fields if not Steadfast
      if (currentPartnerType !== DeliveryPartnerType.Steadfast) {
        delete cleaned.cod_amount;
        delete cleaned.total_lot;
      }

      // Remove Karbari-specific fields if not Karbari
      if (currentPartnerType !== DeliveryPartnerType.Karbari) {
        delete cleaned.karbari_brand_id;
        delete cleaned.karbari_source_id;
        delete cleaned.karbari_agent_id;
        delete cleaned.karbari_courier_id;
        delete cleaned.delivery_zone;
        delete cleaned.karbari_bank_id;
      }

      // Remove Nuport-specific fields if not Nuport
      if (currentPartnerType !== DeliveryPartnerType.Nuport) {
        delete cleaned.nuport_shipping_date;
        delete cleaned.paymentMethod;
        delete cleaned.nuport_courier_id;
        delete cleaned.nuport_source_id;
        delete cleaned.nuport_pickup_location_id;
        delete cleaned.nuport_auto_approve;
        delete cleaned.nuport_free_delivery;
      }

      // Remove BizMotion-specific fields if not BizMotion
      if (currentPartnerType !== DeliveryPartnerType.BizMotion) {
        delete cleaned.bizmotion_status;
        delete cleaned.bizmotion_source;
        delete cleaned.bizmotion_district;
        delete cleaned.bizmotion_city;
        delete cleaned.bizmotion_area;
        delete cleaned.bizmotion_free_delivery;
        delete cleaned.bizmotion_reject_duplicate;
        delete cleaned.payment_method;
      }

      return cleaned;
    };

    // Get the partner type from the selected integration
    const getPartnerTypeFromSelection = (): DeliveryPartnerType | null => {
      if (!selectedDeliveryIntegration.value) return null;
      const [partnerName] = selectedDeliveryIntegration.value.split(":");
      if (partnerName === "pathao") return DeliveryPartnerType.Pathao;
      if (partnerName === "steadfast") return DeliveryPartnerType.Steadfast;
      if (partnerName === "karbari") return DeliveryPartnerType.Karbari;
      if (partnerName === "ecomdrive") return DeliveryPartnerType.EcomDrive;
      if (partnerName === "nuport") return DeliveryPartnerType.Nuport;
      if (partnerName === "bizmotion") return DeliveryPartnerType.BizMotion;
      return null;
    };

    const currentPartnerType = getPartnerTypeFromSelection();

    if (partner === "pathao") {
      let payload: any = {
        ...basePayload,
        store_id: selectedPathaoStore.value,
        recipient_city: Number(pathaoFields.recipient_city) || 0,
        recipient_zone: Number(pathaoFields.recipient_zone) || 0,
        recipient_area: pathaoFields.recipient_area || null,
        item_type: pathaoFields.item_type,
        item_weight: pathaoFields.item_weight,
        amount_to_collect: formData.total ? parseFloat(formData.total) : 0,
        delivery_type: pathaoFields.delivery_type,
      };

      // Clean payload if delivery partner changed
      if (props.isEditMode && props.orderToEdit && currentPartnerType) {
        payload = cleanPayloadForPartnerChange(payload, currentPartnerType);
      }

      if (props.isEditMode && props.orderToEdit) {
        returnResponse = await ordersStore.updateDraftOrder(
          props.orderToEdit.id,
          payload,
        );
      } else {
        returnResponse = isDraft
          ? await createFn(payload)
          : await ordersStore.createPathaoOrder(payload);
      }
    } else if (partner === "steadfast") {
      let payload: any = {
        ...basePayload,
        cod_amount: formData.total ? parseFloat(formData.total) : 0,
        total_lot: steadfastFields.total_lot || 1,
        item_weight: steadfastFields.item_weight || 0.5,
        delivery_type: steadfastFields.delivery_type || 0,
      };

      // Clean payload if delivery partner changed
      if (props.isEditMode && props.orderToEdit && currentPartnerType) {
        payload = cleanPayloadForPartnerChange(payload, currentPartnerType);
      }

      if (props.isEditMode && props.orderToEdit) {
        payload.amount_to_collect = payload.cod_amount;
        delete payload.cod_amount;
        returnResponse = await ordersStore.updateDraftOrder(
          props.orderToEdit.id,
          payload,
        );
      } else if (isDraft) {
        payload.amount_to_collect = payload.cod_amount;
        delete payload.cod_amount;
        returnResponse = await createFn(payload);
      } else {
        returnResponse = await ordersStore.createSteadfastOrder(payload);
      }
    } else if (partner === "karbari") {
      let payload: any = {
        ...basePayload,
        amount_to_collect: formData.total ? parseFloat(formData.total) : 0,
        karbari_brand_id: karbariFields.karbari_brand_id,
        karbari_source_id: karbariFields.karbari_source_id,
        agent_id: useAuthStore().user.id,
        karbari_agent_id: null, // Will be null for now
        karbari_courier_id: karbariFields.karbari_courier_id,
        delivery_zone: karbariFields.delivery_zone,
        delivery_type: karbariFields.delivery_type,
        karbari_bank_id: karbariFields.karbari_bank_id,
      };

      // Clean payload if delivery partner changed
      if (props.isEditMode && props.orderToEdit && currentPartnerType) {
        payload = cleanPayloadForPartnerChange(payload, currentPartnerType);
      }
      console.log("i am here", props.orderToEdit);
      if (props.isEditMode && props.orderToEdit) {
        returnResponse = await ordersStore.updateDraftOrder(
          props.orderToEdit.id,
          payload,
        );
      } else {
        returnResponse = await createFn(payload);
      }
    } else if (partner === "ecomdrive") {
      let payload: any = {
        ...basePayload,
        amount_to_collect: formData.total ? parseFloat(formData.total) : 0,
      };

      // Clean payload if delivery partner changed
      if (props.isEditMode && props.orderToEdit && currentPartnerType) {
        payload = cleanPayloadForPartnerChange(payload, currentPartnerType);
      }

      if (props.isEditMode && props.orderToEdit) {
        returnResponse = await ordersStore.updateDraftOrder(
          props.orderToEdit.id,
          payload,
        );
      } else {
        returnResponse = await createFn(payload);
      }
    } else if (partner === "nuport") {
      let payload: any = {
        ...basePayload,
        // Enforce the free-delivery invariant on the payload itself: free
        // delivery always sends 0, otherwise the entered charge. Guarantees a
        // stale value can never accompany nuport_free_delivery: true.
        delivery_charge: nuportFields.free_delivery
          ? 0
          : formData.deliveryCharge
          ? parseFloat(formData.deliveryCharge)
          : 0,
        amount_to_collect: formData.total ? parseFloat(formData.total) : 0,
        nuport_shipping_date: nuportFields.shippingDate,
        paymentMethod: nuportFields.paymentMethod,
        nuport_courier_id: nuportFields.nuport_courier_id,
        nuport_source: nuportFields.nuport_source_id,
        nuport_pickup_location_id: nuportFields.nuport_pickup_location_id,
        nuport_auto_approve: nuportFields.auto_approve,
        nuport_free_delivery: nuportFields.free_delivery,
      };

      // Clean payload if delivery partner changed
      if (props.isEditMode && props.orderToEdit && currentPartnerType) {
        payload = cleanPayloadForPartnerChange(payload, currentPartnerType);
      }

      if (props.isEditMode && props.orderToEdit) {
        returnResponse = await ordersStore.updateDraftOrder(
          props.orderToEdit.id,
          payload,
        );
      } else {
        returnResponse = await createFn(payload);
      }
    } else if (partner === "bizmotion") {
      const advanceAmount = parseFloat(formData.advance || "0");

      let payload: any = {
        ...basePayload,
        // Enforce the free-delivery invariant on the payload itself: free
        // delivery always sends 0, otherwise the entered charge. Guarantees a
        // stale value can never accompany bizmotion_free_delivery: true.
        delivery_charge: bizmotionFields.free_delivery
          ? 0
          : formData.deliveryCharge
          ? parseFloat(formData.deliveryCharge)
          : 0,
        amount_to_collect: formData.total ? parseFloat(formData.total) : 0,
        // Empty strings would be filed as a blank status/source rather than
        // omitted, so send null and let BizMotion apply its own default.
        bizmotion_status: bizmotionFields.bizmotion_status || null,
        bizmotion_source: bizmotionFields.bizmotion_source || null,
        bizmotion_district: bizmotionFields.bizmotion_district || null,
        bizmotion_city: bizmotionFields.bizmotion_city || null,
        bizmotion_area: bizmotionFields.bizmotion_area || null,
        bizmotion_free_delivery: bizmotionFields.free_delivery,
        bizmotion_reject_duplicate: bizmotionFields.reject_duplicate,
        // The payment method only means something against an advance, so it is
        // only sent when there is one to record.
        payment_method:
          advanceAmount > 0 ? bizmotionFields.paymentMethod : null,
      };

      // Clean payload if delivery partner changed
      if (props.isEditMode && props.orderToEdit && currentPartnerType) {
        payload = cleanPayloadForPartnerChange(payload, currentPartnerType);
      }

      if (props.isEditMode && props.orderToEdit) {
        returnResponse = await ordersStore.updateDraftOrder(
          props.orderToEdit.id,
          payload,
        );
      } else {
        returnResponse = await createFn(payload);
      }
    } else {
      // No delivery integration — persist the order as-is. Its
      // delivery_partner_integration_id is already absent from basePayload.
      const payload: any = {
        ...basePayload,
        amount_to_collect: formData.total ? parseFloat(formData.total) : 0,
      };

      if (props.isEditMode && props.orderToEdit) {
        returnResponse = await ordersStore.updateDraftOrder(
          props.orderToEdit.id,
          payload,
        );
      } else {
        returnResponse = await createFn(payload);
      }
    }
    const { response, error } = returnResponse;
    const saved = !error && !!response;

    if (error) {
      const errorData = error.response?.data;
      const errorMessage = errorData?.message || "Failed to save order";
      saveError.value = errorMessage;

      // A stock refusal means somebody else took the last unit while this form
      // was open, so the counts on screen are already wrong. Re-read them and
      // the card turns itself out-of-stock.
      if (errorData?.errors?.products) {
        await productStore.getAllProducts();
      }
    } else if (response) {
      const successMessage = props.isEditMode
        ? "Draft order updated successfully"
        : isDraft
        ? "Draft order created successfully"
        : "Delivery order created successfully";
      ElNotification.success(successMessage);
      emit("set-ordermessage", { messageBody, orderData: response.data.order });
    }

    // The lock comes off only when nothing was saved. On success this form has
    // done its one job and the parent is about to take it away — handing the
    // button back for the frames between `close-modal` and that unmount is what
    // let a second click through, and a second click here is a second real order
    // and a second confirmation message to the customer. Whether the parent
    // unmounts on the same tick or waits out a dialog fade is not something this
    // form can see, so it stops relying on it.
    if (!saved) formSubmitting.value = false;

    // The dialog goes away only when the order actually saved. A refusal — the
    // store has no stock to spare, a field the server rejected, a dropped
    // connection — leaves the form standing with everything already typed into
    // it, so the one thing at fault can be corrected and sent again. Closing
    // here regardless threw the whole order away and left the agent to retype
    // it from memory, with the customer still on the line.
    if (saved) emit("close-modal");
  });
};

onMounted(async () => {
  await productStore.getAllProducts();

  // If in edit mode, populate form with order data
  if (props.isEditMode && props.orderToEdit) {
    const order = props.orderToEdit;

    // Set basic form data
    formData.name = order.recipient_name || "";
    formData.phone = order.recipient_phone || "";
    formData.address.street = order.recipient_address || "";
    formData.deliveryCharge = order.delivery_charge?.toString() || "0";
    formData.discount = order.discount?.toString() || "0";
    formData.advance = order.advance?.toString() || "0";
    formData.sub_total = order.sub_total?.toString() || "0";
    formData.delivery_partner_integration_id =
      order.delivery_partner_integration_id;

    // Set products
    if (order.products && order.products.length > 0) {
      formData.products = order.products.map((p: any) => {
        const unitPrice = p.pivot?.unit_price || 0;
        const quantity = p.pivot?.quantity || 1;
        const discountType = fromDiscountTypeEnum(p.pivot?.discount_type);
        const discountAmount = p.pivot?.discount_amount || 0;
        return {
          product_id: p.id,
          quantity,
          unit_price: unitPrice,
          price: unitPrice * quantity,
          discount_type: discountType,
          discount_amount: discountAmount,
        };
      });
    }

    // Set partner-specific fields based on delivery_partner_integration type
    const partnerType = order.delivery_partner_integration?.type;
    if (partnerType === DeliveryPartnerType.Pathao) {
      selectedPathaoStore.value = order.store_id;
      pathaoFields.recipient_city = order.recipient_city?.toString() || "";
      pathaoFields.recipient_zone = order.recipient_zone?.toString() || "";
      pathaoFields.recipient_area = order.recipient_area || "";
      pathaoFields.item_type = order.item_type || 2;
      pathaoFields.item_weight = order.item_weight || 0.5;
      pathaoFields.delivery_type = order.delivery_type || 48;
    } else if (partnerType === DeliveryPartnerType.Steadfast) {
      steadfastFields.item_weight = order.item_weight || 0.5;
      steadfastFields.item_description = order.item_description || "";
      steadfastFields.total_lot = order.total_lot || 1;
      steadfastFields.delivery_type = order.delivery_type || 0;
    } else if (partnerType === DeliveryPartnerType.Karbari) {
      karbariFields.karbari_brand_id = order.karbari_brand_id;
      karbariFields.karbari_source_id = order.karbari_source_id;
      karbariFields.karbari_courier_id = order.karbari_courier_id;
      karbariFields.delivery_zone = order.delivery_zone || 1;
      karbariFields.delivery_type = order.delivery_type || 0;
      karbariFields.karbari_bank_id = order.karbari_bank_id;
    } else if (partnerType === DeliveryPartnerType.Nuport) {
      nuportFields.shippingDate = order.nuport_shipping_date || null;
      nuportFields.paymentMethod = order.paymentMethod || "Cash";
      nuportFields.nuport_courier_id = order.nuport_courier_id || null;
      nuportFields.nuport_source_id = order.nuport_source || null;
      nuportFields.nuport_pickup_location_id =
        order.nuport_pickup_location_id || null;
      nuportFields.auto_approve = order.nuport_auto_approve ?? true;
      nuportFields.free_delivery = order.nuport_free_delivery ?? false;
    } else if (partnerType === DeliveryPartnerType.BizMotion) {
      bizmotionFields.bizmotion_status = order.bizmotion_status || "";
      bizmotionFields.bizmotion_source = order.bizmotion_source || "";
      bizmotionFields.bizmotion_district = order.bizmotion_district || "";
      bizmotionFields.bizmotion_city = order.bizmotion_city || "";
      bizmotionFields.bizmotion_area = order.bizmotion_area || "";
      bizmotionFields.paymentMethod =
        order.payment_method || "Cash on Delivery";
      bizmotionFields.free_delivery = order.bizmotion_free_delivery ?? false;
      bizmotionFields.reject_duplicate =
        order.bizmotion_reject_duplicate ?? false;
    }

    // Set remarks
    if (order.remarks) {
      finalMessage.value = order.remarks;
    }

    // Load profile if exists
    if (order.profile_id) {
      const profile = await profileStore.getProfile(order.profile_id);
      if (profile) {
        originalProfile.value = { ...profile };
        // Email isn't stored on the order, so read it from the profile.
        formData.email =
          (profile as any).emails?.find((e: any) => e.is_default)?.email ||
          (profile as any).emails?.[0]?.email ||
          "";
      }
    }

    // fetch integrations for delivery partners using unified API
    loadingIntegrations.value = true;
    try {
      const allIntegrations =
        (await ordersStore.getDeliveryPartnerIntegrations()) as any;
      if (allIntegrations) {
        const integrationArray = Array.isArray(allIntegrations)
          ? allIntegrations
          : allIntegrations.data || [allIntegrations];

        // Separate by type for backward compatibility
        pathaoStores.value = integrationArray.filter(
          (i: any) => i.type === DeliveryPartnerType.Pathao,
        );
        steadfastStores.value = integrationArray.filter(
          (i: any) => i.type === DeliveryPartnerType.Steadfast,
        );
        karbariStores.value = integrationArray.filter(
          (i: any) => i.type === DeliveryPartnerType.Karbari,
        );
        ecomdriveStores.value = integrationArray.filter(
          (i: any) => i.type === DeliveryPartnerType.EcomDrive,
        );
        nuportStores.value = integrationArray.filter(
          (i: any) => i.type === DeliveryPartnerType.Nuport,
        );
        bizmotionStores.value = integrationArray.filter(
          (i: any) => i.type === DeliveryPartnerType.BizMotion,
        );

        deliveryIntegrations.value = [
          ...pathaoStores.value.map((s: any) => ({
            partner: "pathao",
            store: s,
            value: `pathao:${s.id}:${s.name || ""}`,
            label: s.name || `Pathao Integration ${s.id}`,
          })),
          ...steadfastStores.value.map((s: any) => ({
            partner: "steadfast",
            store: s,
            value: `steadfast:${s.id}:${s.name || ""}`,
            label: s.name || `Steadfast Integration ${s.id}`,
          })),
          ...karbariStores.value.map((s: any) => ({
            partner: "karbari",
            store: s,
            value: `karbari:${s.id}:${s.name || ""}`,
            label: s.name || `Karbari Integration ${s.id}`,
            metadata: s.metadata,
          })),
          ...ecomdriveStores.value.map((s: any) => ({
            partner: "ecomdrive",
            store: s,
            value: `ecomdrive:${s.id}:${s.name || ""}`,
            label: s.name || `EcomDrive Integration ${s.id}`,
          })),
          ...nuportStores.value.map((s: any) => ({
            partner: "nuport",
            store: s,
            value: `nuport:${s.id}:${s.name || ""}`,
            label: s.name || `Nuport Integration ${s.id}`,
          })),
          ...bizmotionStores.value.map((s: any) => ({
            partner: "bizmotion",
            store: s,
            value: `bizmotion:${s.id}:${s.name || ""}`,
            label: s.name || `BizMotion Integration ${s.id}`,
          })),
        ];

        // Set the selected delivery integration after integrations are loaded
        // Find the matching integration based on partner type and integration ID
        const matchingIntegration = deliveryIntegrations.value.find(
          (integration: any) =>
            integration.store.type ===
              order.delivery_partner_integration?.type &&
            integration.store.id === order.delivery_partner_integration_id,
        );

        if (matchingIntegration) {
          selectedDeliveryIntegration.value = matchingIntegration.value;
        } else {
          // If no matching integration found, select the default one
          const defaultIntegration = deliveryIntegrations.value.find(
            (integration: any) => integration.store.is_default === true,
          );
          if (defaultIntegration) {
            selectedDeliveryIntegration.value = defaultIntegration.value;
          }
        }
      }
    } catch (e) {
      console.error("Error fetching delivery integrations", e);
    } finally {
      loadingIntegrations.value = false;
    }
  } else {
    // Original logic for create mode
    const profileId = ordersStore.selectedProfileForOrder.id;
    profileStore._fraudCheckerData = null;
    if (profileId) {
      const profile = await profileStore.getProfile(profileId);
      if (profile) {
        await populateFromProfile(profile);
      }
    }

    // fetch integrations for delivery partners using unified API
    loadingIntegrations.value = true;
    try {
      const allIntegrations =
        (await ordersStore.getDeliveryPartnerIntegrations()) as any;
      if (allIntegrations) {
        const integrationArray = Array.isArray(allIntegrations)
          ? allIntegrations
          : allIntegrations.data || [allIntegrations];

        // Separate by type for backward compatibility
        pathaoStores.value = integrationArray.filter(
          (i: any) => i.type === DeliveryPartnerType.Pathao,
        );
        steadfastStores.value = integrationArray.filter(
          (i: any) => i.type === DeliveryPartnerType.Steadfast,
        );
        karbariStores.value = integrationArray.filter(
          (i: any) => i.type === DeliveryPartnerType.Karbari,
        );
        ecomdriveStores.value = integrationArray.filter(
          (i: any) => i.type === DeliveryPartnerType.EcomDrive,
        );
        nuportStores.value = integrationArray.filter(
          (i: any) => i.type === DeliveryPartnerType.Nuport,
        );
        bizmotionStores.value = integrationArray.filter(
          (i: any) => i.type === DeliveryPartnerType.BizMotion,
        );

        deliveryIntegrations.value = [
          ...pathaoStores.value.map((s: any) => ({
            partner: "pathao",
            store: s,
            value: `pathao:${s.id}:${s.name || ""}`,
            label: s.name || `Pathao Integration ${s.id}`,
          })),
          ...steadfastStores.value.map((s: any) => ({
            partner: "steadfast",
            store: s,
            value: `steadfast:${s.id}:${s.name || ""}`,
            label: s.name || `Steadfast Integration ${s.id}`,
          })),
          ...karbariStores.value.map((s: any) => ({
            partner: "karbari",
            store: s,
            value: `karbari:${s.id}:${s.name || ""}`,
            label: s.name || `Karbari Integration ${s.id}`,
            metadata: s.metadata,
          })),
          ...ecomdriveStores.value.map((s: any) => ({
            partner: "ecomdrive",
            store: s,
            value: `ecomdrive:${s.id}:${s.name || ""}`,
            label: s.name || `EcomDrive Integration ${s.id}`,
          })),
          ...nuportStores.value.map((s: any) => ({
            partner: "nuport",
            store: s,
            value: `nuport:${s.id}:${s.name || ""}`,
            label: s.name || `Nuport Integration ${s.id}`,
          })),
          ...bizmotionStores.value.map((s: any) => ({
            partner: "bizmotion",
            store: s,
            value: `bizmotion:${s.id}:${s.name || ""}`,
            label: s.name || `BizMotion Integration ${s.id}`,
          })),
        ];

        // Auto-select the default delivery integration if not in edit mode
        if (!selectedDeliveryIntegration.value) {
          const defaultIntegration = deliveryIntegrations.value.find(
            (integration: any) => integration.store.is_default === true,
          );
          if (defaultIntegration) {
            selectedDeliveryIntegration.value = defaultIntegration.value;
          }
        }
      }
    } catch (e) {
      console.error("Error fetching delivery integrations", e);
    } finally {
      loadingIntegrations.value = false;
    }
  }
});

// Pathao city/zone/area helpers
const cities = ref<any[]>([]);
const zones = ref<any[]>([]);
const areas = ref<any[]>([]);
const loadingCities = ref(false);
const loadingZones = ref(false);
const loadingAreas = ref(false);

const fetchCities = async () => {
  loadingCities.value = true;
  const res = (await ordersStore.getPathaoCities()) as any;
  cities.value = res?.data?.data || res?.data || res || [];
  loadingCities.value = false;
};
const fetchZones = async (cityId: any) => {
  if (!cityId) return;
  loadingZones.value = true;
  const res = (await ordersStore.getPathaoZones(cityId)) as any;
  zones.value = res?.data?.data || res?.data || res || [];
  loadingZones.value = false;
};
const fetchAreas = async (zoneId: any) => {
  if (!zoneId) return;
  loadingAreas.value = true;
  const res = (await ordersStore.getPathaoAreas(zoneId)) as any;
  areas.value = res?.data?.data || res?.data || res || [];
  loadingAreas.value = false;
};

const onPathaoCityChange = (val: any) => {
  const cityName = cities.value.find((c) => (c.city_id || c.id) === val)
    ?.city_name;
  formData.address.state = cityName || "";
  // fetch zones for selected city
  if (val) fetchZones(val);
};

const fetchPathaoStores = async () => {
  loadingPathaoStores.value = true;
  try {
    const res = (await ordersStore.getPathaoStores()) as any;
    pathaoStoresList.value = res?.data || res || [];
  } catch (e) {
    console.error("Error fetching Pathao stores", e);
  } finally {
    loadingPathaoStores.value = false;
  }
};

watch(selectedDeliveryIntegration, (val: any) => {
  if (!val) {
    // Cleared selection → drop the integration id so the order is persisted
    // without a delivery partner.
    formData.delivery_partner_integration_id = undefined;
    return;
  }

  const [partnerType, integrationId] = val.split(":");

  // Set the delivery_partner_integration_id in formData
  formData.delivery_partner_integration_id = integrationId
    ? parseInt(integrationId, 10)
    : undefined;

  if (partnerType === "pathao") {
    // prefetch cities and stores
    fetchCities();
    fetchPathaoStores();
  }

  // For a NEW order, default the approval switch from the selected Nuport
  // integration. In edit mode we keep the order's own stored value.
  if (partnerType === "nuport" && !props.isEditMode) {
    const integration = nuportStores.value.find(
      (s) => s.id === formData.delivery_partner_integration_id,
    );
    nuportFields.auto_approve = integration?.nuport_auto_approve ?? true;
  }

  // Same for BizMotion: a new order starts from the integration's defaults, an
  // order being edited keeps whatever it was saved with.
  if (partnerType === "bizmotion" && !props.isEditMode) {
    const integration = bizmotionStores.value.find(
      (s) => s.id === formData.delivery_partner_integration_id,
    );
    bizmotionFields.bizmotion_status =
      integration?.bizmotion_default_status || "";
    bizmotionFields.reject_duplicate =
      integration?.bizmotion_reject_duplicate ?? false;
  }
});

// Debounced phone watcher: call fraud check when the user has typed 11 digits
let phoneDebounceTimer: ReturnType<typeof setTimeout> | null = null;
watch(
  () => formData.phone,
  (val: string | undefined) => {
    if (phoneDebounceTimer) clearTimeout(phoneDebounceTimer);
    const phoneStr = String(val || "").replace(/\D/g, "");
    const len = phoneStr.length;

    if (len === 0) {
      showPhoneLengthInfo.value = false;
      return;
    }

    if (len < 11) {
      showPhoneLengthInfo.value = true;
      return;
    }

    if (len === 11) {
      showPhoneLengthInfo.value = false;
      phoneDebounceTimer = setTimeout(() => fetchFraudInfo(phoneStr), 400);
    }
  },
);

const calculatedSubTotal = computed(() => {
  return calculateSubtotals().toFixed(2);
});

const calculatedTotal = computed(() => {
  const subtotal = calculateSubtotals();
  const delivery = parseFloat(formData.deliveryCharge || "0");
  const discount = parseFloat(formData.discount || "0");
  const advance = parseFloat(formData.advance || "0");
  return (subtotal + delivery - discount - advance).toFixed(2);
});

// removed unused helper getProductPrice

/**
 * Calculate the discount value per unit based on discount type
 * @param unitPrice - The price per unit
 * @param discountAmount - The discount amount (percentage value or fixed amount)
 * @param discountType - The type of discount (1 = fixed, 2 = percentage, null = no discount)
 * @returns The calculated discount value per unit
 */
const calculateDiscountPerUnit = (
  unitPrice: number,
  discountAmount: number,
  discountType: ProductDiscountType,
): number => {
  if (!discountType || discountAmount === 0) {
    return 0;
  }
  if (discountType === 2) {
    // percentage: calculate percentage off unit price
    return (unitPrice * discountAmount) / 100;
  }
  // fixed: discount amount per unit
  return discountAmount;
};

/**
 * Calculate the total discount value for the entire line item
 * @param unitPrice - The price per unit
 * @param quantity - The quantity of items
 * @param discountAmount - The discount amount (percentage value or fixed amount)
 * @param discountType - The type of discount (1 = fixed, 2 = percentage, null = no discount)
 * @returns The total discount value for all units
 */
const calculateTotalDiscountValue = (
  unitPrice: number,
  quantity: number,
  discountAmount: number,
  discountType: ProductDiscountType,
): number => {
  const discountPerUnit = calculateDiscountPerUnit(
    unitPrice,
    discountAmount,
    discountType,
  );
  return discountPerUnit * quantity;
};

/**
 * Calculate the final price after discount for a product item
 * Formula: (unit_price - discount_per_unit) * quantity
 * @param item - The product item with pricing and discount details
 * @returns The final price after applying discount
 */
const calculateProductTotal = (item: any): number => {
  const unitPrice = item.unit_price || 0;
  const quantity = item.quantity || 1;
  const discountAmount = item.discount_amount || 0;
  const discountType: ProductDiscountType = item.discount_type;

  const discountPerUnit = calculateDiscountPerUnit(
    unitPrice,
    discountAmount,
    discountType,
  );
  const pricePerUnitAfterDiscount = unitPrice - discountPerUnit;

  return pricePerUnitAfterDiscount * quantity;
};

/**
 * Convert backend discount type enum to frontend type
 * @param enumValue - Backend enum value (1, 2, or null/undefined)
 * @returns Frontend discount type (1, 2, or null)
 */
const fromDiscountTypeEnum = (
  enumValue: number | null | undefined,
): ProductDiscountType => {
  if (enumValue === 1 || enumValue === 2) {
    return enumValue;
  }
  return null;
};

const calculateSubtotals = () => {
  return formData.products.reduce((sum, item) => {
    return sum + calculateProductTotal(item);
  }, 0);
};
const isKarbariCourierPathao = computed(() => {
  const selectedKarbariCourier = karbariCouriers.value.find(
    (c: any) => c.id === karbariFields.karbari_courier_id,
  );
  return selectedKarbariCourier?.module_id === 1;
});

watch(
  () => formData.products.map((p) => p.product_id),
  (productIds, _oldProductIds) => {
    productIds.forEach((id, index) => {
      if (id && !formData.products[index].price) {
        const product = productStore.allProducts.find((p) => p.id === id);
        if (product) {
          formData.products[index].price = effectiveUnitPrice(product);
        }
      }
    });
  },
  { deep: true },
);

// watch pathao city/zone to fetch zones/areas
watch(
  () => pathaoFields.recipient_city,
  (val) => {
    pathaoFields.recipient_zone = "";
    pathaoFields.recipient_area = "";
    zones.value = [];
    areas.value = [];
    if (val) fetchZones(val);
  },
);
watch(
  () => pathaoFields.recipient_zone,
  (val) => {
    pathaoFields.recipient_area = "";
    areas.value = [];
    if (val) fetchAreas(val);
  },
);

watch(calculatedSubTotal, (newVal) => {
  formData.sub_total = newVal;
});

watch(calculatedTotal, (newVal) => {
  formData.total = newVal;
});

// When free delivery is enabled, force the delivery charge to 0 so the stored
// value, the order total, and the amount sent to the partner all agree. Watch
// the partner too, so re-selecting it (e.g. after switching partners in edit
// mode) re-applies the invariant even though free_delivery itself didn't change.
watch(
  () => [
    nuportFields.free_delivery,
    bizmotionFields.free_delivery,
    selectedDeliveryIntegrationPartner.value,
  ],
  ([isNuportFree, isBizMotionFree, partner]) => {
    if (
      (partner === "nuport" && isNuportFree) ||
      (partner === "bizmotion" && isBizMotionFree)
    ) {
      formData.deliveryCharge = "0";
    }
  },
);

// Auto-select Karbari defaults
watch(
  karbariSources,
  (sources) => {
    if (!karbariFields.karbari_source_id && sources && sources.length > 0) {
      const smartcommSource = sources.find(
        (s: any) => s.source_name === "SmartComm",
      );
      if (smartcommSource) {
        karbariFields.karbari_source_id = smartcommSource.id;
      }
    }
  },
  { immediate: true },
);

watch(
  karbariBrands,
  (brands) => {
    if (!karbariFields.karbari_brand_id && brands && brands.length === 1) {
      karbariFields.karbari_brand_id = brands[0].id;
    }
  },
  { immediate: true },
);

watch(
  karbariCouriers,
  (couriers) => {
    if (
      !karbariFields.karbari_courier_id &&
      couriers &&
      couriers.length === 1
    ) {
      karbariFields.karbari_courier_id = couriers[0].id;
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="px-2 py-2 relative">
    <div class="max-h-[70vh] overflow-y-auto pr-4">
      <el-form ref="formRef" :model="formData" :rules="rules" class="space-y-4">
        <!-- <div class="flex items-center justify-start gap-2">
          <span>Order_No#</span>
          <span>123</span>
        </div> -->

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <!-- ===================== -->
          <!-- Delivery Success Check -->
          <!-- ===================== -->
          <div
            class="order-1 lg:order-2 col-span-1 lg:col-span-4 border rounded-lg p-2 sm:p-4 bg-[#d7d7e7]/[.5] border-gray-400"
          >
            <h3 class="text-base sm:text-lg font-semibold mb-3">
              Delivery Success Check
            </h3>

            <div
              v-if="!showPhoneLengthInfo"
              class="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <div
                class="p-2 pl-4 bg-white shadow rounded-xl border dark:bg-dark-blue dark:text-light-font dark:border-dark-primary-border"
              >
                <div class="flex flex-col">
                  <p class="text-sm text-left">Parcels</p>
                  <p class="my-1 text-xl font-bold text-left text-[#8D87FF]">
                    <template v-if="fraudInfoLoading">
                      <loader />
                    </template>
                    <template v-else>
                      {{ profileStore._fraudCheckerData?.total_parcels ?? 0 }}
                    </template>
                  </p>
                </div>
              </div>

              <div
                class="p-2 pl-4 bg-white shadow rounded-xl border dark:bg-dark-blue dark:text-light-font dark:border-dark-primary-border"
              >
                <div class="flex flex-col">
                  <p class="text-sm text-left">Delivered</p>
                  <p class="my-1 text-xl font-bold text-left text-[#8D87FF]">
                    <template v-if="fraudInfoLoading">
                      <loader />
                    </template>
                    <template v-else>
                      {{ profileStore._fraudCheckerData?.total_delivered ?? 0 }}
                    </template>
                  </p>
                </div>
              </div>

              <div
                class="p-2 pl-4 bg-white shadow rounded-xl border dark:bg-dark-blue dark:text-light-font dark:border-dark-primary-border"
              >
                <div class="flex flex-col">
                  <p class="text-sm text-left">Cancelled</p>
                  <p class="my-1 text-xl font-bold text-left text-[#8D87FF]">
                    <template v-if="fraudInfoLoading">
                      <loader />
                    </template>
                    <template v-else>
                      {{ profileStore._fraudCheckerData?.total_cancelled ?? 0 }}
                    </template>
                  </p>
                </div>
              </div>

              <div
                class="p-2 pl-4 bg-white shadow rounded-xl border dark:bg-dark-blue dark:text-light-font dark:border-dark-primary-border"
              >
                <div class="flex flex-col">
                  <p class="text-sm text-left">Percentage</p>
                  <p class="my-1 text-lg font-bold text-left text-[#8D87FF]">
                    <template v-if="fraudInfoLoading">
                      <loader />
                    </template>
                    <template v-else>
                      {{
                        profileStore._fraudCheckerData?.success_percentage ?? 0
                      }}%
                    </template>
                  </p>
                </div>
              </div>
            </div>

            <div v-if="showPhoneLengthInfo" class="text-xs text-gray-500 mt-1">
              Phone number should be 11 digits.
            </div>
          </div>

          <!-- ================= -->
          <!-- Main Form Section -->
          <!-- ================= -->
          <div class="order-2 lg:order-1 col-span-1 lg:col-span-8">
            <div v-if="showProfileSelect" class="mb-3">
              <div class="form-elem-container">
                <FormLabel :required="true">Customer Profile</FormLabel>
                <div class="flex items-center gap-2">
                  <el-select
                    v-model="selectedProfileId"
                    filterable
                    clearable
                    class="w-full"
                    placeholder="Search by name, phone or email"
                    remote
                    reserve-keyword
                    :remote-method="remoteMethod"
                    :loading="loading"
                    @change="onProfileSelected"
                  >
                    <el-option
                      v-for="profile in profileStore.simpleProfiles"
                      :key="profile.id"
                      :label="
                        profile.default_phone
                          ? `${profile.name} | ${profile.default_phone.phone}`
                          : profile.name
                      "
                      :value="profile.id"
                    />
                  </el-select>
                  <div class="ml-auto !w-auto flex !justify-end">
                    <ProfileAddButton @created="onProfileCreated" />
                  </div>
                </div>
              </div>
            </div>
            <div class="flex flex-col lg:flex-row gap-4">
              <el-form-item
                v-if="showRecipientFields"
                prop="name"
                class="flex-1"
              >
                <div class="form-elem-container">
                  <FormLabel :required="true">Name</FormLabel>
                  <el-input v-model="formData.name" placeholder="Enter name" />
                </div>
              </el-form-item>

              <el-form-item
                v-if="showRecipientFields"
                prop="phone"
                class="flex-1"
              >
                <div class="form-elem-container">
                  <FormLabel :required="true">Phone</FormLabel>
                  <el-input
                    v-model="formData.phone"
                    placeholder="Enter phone"
                  />
                </div>
              </el-form-item>

              <el-form-item
                v-if="showRecipientFields"
                prop="email"
                class="flex-1"
              >
                <div class="form-elem-container">
                  <FormLabel>Email</FormLabel>
                  <el-input
                    v-model="formData.email"
                    placeholder="Enter email"
                  />
                </div>
              </el-form-item>

              <div v-if="hasDeliveryIntegrations" class="flex-1">
                <div class="form-elem-container">
                  <FormLabel>Delivery Integration</FormLabel>
                  <el-select
                    v-model="selectedDeliveryIntegration"
                    placeholder="Select delivery integration"
                    clearable
                  >
                    <el-option
                      v-for="(opt, idx) in deliveryIntegrations"
                      :key="idx"
                      :label="opt.label"
                      :value="opt.value"
                    >
                      <template #default>
                        <div class="flex items-center gap-2">
                          <img
                            v-if="opt.partner"
                            :src="getDeliveryIntegrationIcon(opt.partner)"
                            alt=""
                            class="w-4 h-4"
                          />
                          <span>{{ opt.label }}</span>
                        </div>
                      </template>
                    </el-option>
                  </el-select>
                </div>
              </div>
            </div>
            <div v-if="showStatusSelect" class="mt-2">
              <div class="form-elem-container">
                <FormLabel :required="true">Status</FormLabel>
                <el-select
                  v-model="formData.status"
                  placeholder="Select status"
                  class="w-full"
                >
                  <el-option
                    :label="DeliveryStatusLabels[DeliveryStatus.Draft]"
                    :value="DeliveryStatus.Draft"
                  />
                  <el-option
                    :label="DeliveryStatusLabels[DeliveryStatus.Confirmed]"
                    :value="DeliveryStatus.Confirmed"
                  />
                </el-select>
              </div>
            </div>
            <div class="mt-2">
              <el-form-item prop="address.street">
                <div class="form-elem-container">
                  <FormLabel :required="hasDeliveryIntegrations"
                    >Address</FormLabel
                  >
                  <el-input
                    v-model="formData.address.street"
                    type="textarea"
                    :rows="2"
                    placeholder="Enter address"
                    class="!text-sm"
                  />
                </div>
              </el-form-item>
            </div>
            <!-- Pathao -->
            <div
              v-if="
                selectedDeliveryIntegration &&
                selectedDeliveryIntegrationPartner === 'pathao'
              "
              class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2"
            >
              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel :required="true">Pathao Store</FormLabel>
                  <el-select
                    v-model="selectedPathaoStore"
                    filterable
                    placeholder="Select Pathao Store"
                    :loading="loadingPathaoStores"
                  >
                    <el-option
                      v-for="store in pathaoStoresList"
                      :key="store.store_id || store.id"
                      :label="store.store_name || store.name"
                      :value="store.store_id || store.id"
                    />
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel :required="true">City</FormLabel>
                  <el-select
                    v-model="pathaoFields.recipient_city"
                    filterable
                    placeholder="Select City"
                    :loading="loadingCities"
                    @change="onPathaoCityChange"
                  >
                    <el-option
                      v-for="city in cities"
                      :key="city.city_id || city.id"
                      :label="city.city_name || city.name"
                      :value="city.city_id || city.id"
                    />
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel :required="true">Zone</FormLabel>
                  <el-select
                    v-model="pathaoFields.recipient_zone"
                    filterable
                    placeholder="Select Zone"
                    :loading="loadingZones"
                    :disabled="!pathaoFields.recipient_city"
                  >
                    <el-option
                      v-for="zone in zones"
                      :key="zone.zone_id || zone.id"
                      :label="zone.zone_name || zone.name"
                      :value="zone.zone_id || zone.id"
                    />
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel :required="true">Area</FormLabel>
                  <el-select
                    v-model="pathaoFields.recipient_area"
                    filterable
                    placeholder="Select Area"
                    :loading="loadingAreas"
                    :disabled="!pathaoFields.recipient_zone"
                  >
                    <el-option
                      v-for="area in areas"
                      :key="area.area_id || area.id"
                      :label="area.area_name || area.name"
                      :value="area.area_id || area.id"
                    />
                  </el-select>
                </div>
              </el-form-item>
            </div>

            <!-- Karbari -->
            <div
              v-if="
                selectedDeliveryIntegration &&
                selectedDeliveryIntegrationPartner === 'karbari'
              "
              class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2"
            >
              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel :required="true">Brand</FormLabel>
                  <el-select v-model="karbariFields.karbari_brand_id">
                    <el-option
                      v-for="brand in karbariBrands"
                      :key="brand.id"
                      :label="brand.brand_name"
                      :value="brand.id"
                    />
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel :required="true">Source</FormLabel>
                  <el-select v-model="karbariFields.karbari_source_id">
                    <el-option
                      v-for="source in karbariSources"
                      :key="source.id"
                      :label="source.source_name"
                      :value="source.id"
                    />
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel :required="true">Courier</FormLabel>
                  <el-select v-model="karbariFields.karbari_courier_id">
                    <el-option
                      v-for="courier in karbariCouriers"
                      :key="courier.id"
                      :label="courier.courier_name"
                      :value="courier.id"
                    >
                      <template #default>
                        <div class="flex justify-between items-center w-full">
                          <span>{{ courier.courier_name }}</span>
                          <span class="text-xs text-gray-500">
                            In: {{ courier.in_zone_cost }} | Out:
                            {{ courier.out_zone_cost }}
                          </span>
                        </div>
                      </template>
                    </el-option>
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item class="lg:col-span-2">
                <div class="form-elem-container">
                  <FormLabel :required="true">Delivery Zone</FormLabel>
                  <el-radio-group v-model="karbariFields.delivery_zone">
                    <el-radio :label="1">Inside Dhaka/CTG</el-radio>
                    <el-radio :label="2">Outside Dhaka/CTG</el-radio>
                    <el-radio :label="3">Sub Area Dhaka/CTG</el-radio>
                  </el-radio-group>
                </div>
              </el-form-item>

              <el-form-item v-if="isKarbariCourierPathao">
                <div class="form-elem-container">
                  <FormLabel :required="true">Delivery Type</FormLabel>
                  <el-select v-model="karbariFields.delivery_type">
                    <el-option label="Regular" :value="0" />
                    <el-option label="Express" :value="1" />
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel :required="false">Bank Account</FormLabel>
                  <el-select v-model="karbariFields.karbari_bank_id" clearable>
                    <el-option
                      v-for="bank in karbariBankAccounts"
                      :key="bank.id"
                      :label="bank.title"
                      :value="bank.id"
                    />
                  </el-select>
                </div>
              </el-form-item>
            </div>
            <!-- Nuport -->
            <div
              v-if="
                selectedDeliveryIntegration &&
                selectedDeliveryIntegrationPartner === 'nuport'
              "
              class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2"
            >
              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel :required="true">Couriers</FormLabel>
                  <el-select v-model="nuportFields.nuport_courier_id">
                    <el-option
                      v-for="courier in nuportCouriers"
                      :key="courier.id"
                      :label="courier.name"
                      :value="courier.id"
                    >
                      <template #default>
                        <div class="flex justify-between items-center w-full">
                          <span>{{ courier.name }}</span>
                          <span class="text-xs text-gray-500">
                            {{ courier.partnerId }}
                          </span>
                        </div>
                      </template>
                    </el-option>
                  </el-select>
                </div>
              </el-form-item>
              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel :required="true">Order Source</FormLabel>
                  <el-select
                    v-model="nuportFields.nuport_source_id"
                    placeholder="Select order source"
                  >
                    <el-option
                      v-for="(source, idx) in nuportOrderSources"
                      :key="idx"
                      :label="getNuportOrderSourceLabel(source)"
                      :value="getNuportOrderSourceValue(source)"
                    />
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel>Pickup Location</FormLabel>
                  <el-select
                    v-model="nuportFields.nuport_pickup_location_id"
                    placeholder="Default (latest warehouse)"
                    filterable
                    clearable
                  >
                    <el-option
                      v-for="location in nuportPickupLocations"
                      :key="location.id"
                      :label="getNuportPickupLocationLabel(location)"
                      :value="location.id"
                    />
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel :required="true">Payment Methods</FormLabel>
                  <el-select
                    v-model="nuportFields.paymentMethod"
                    placeholder="Select payment method"
                  >
                    <el-option label="Cash" value="Cash" />
                    <el-option
                      label="Cash On Delivery (COD)"
                      value="Cash On Delivery (COD)"
                    />
                    <el-option label="Cheque" value="Cheque" />
                    <el-option label="Card" value="Card" />
                    <el-option label="Net Banking" value="Net Banking" />
                    <el-option label="Bkash" value="Bkash" />
                    <el-option label="Rocket" value="Rocket" />
                    <el-option label="Nagad" value="Nagad" />
                    <el-option label="Upay" value="Upay" />
                    <el-option label="Tap" value="Tap" />
                    <el-option label="SSL Commerz" value="SSL Commerz" />
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel>Shipping Date</FormLabel>
                  <el-date-picker
                    v-model="nuportFields.shippingDate"
                    type="datetime"
                    placeholder="Optional — sent to Nuport as order date"
                    format="YYYY-MM-DD HH:mm:ss"
                    value-format="YYYY-MM-DD HH:mm:ss"
                    clearable
                    class="w-full"
                  />
                </div>
              </el-form-item>

              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel>Order Approval</FormLabel>
                  <div class="flex items-center gap-3 h-8">
                    <el-switch
                      v-model="nuportFields.auto_approve"
                      active-text="Approval"
                      inactive-text="Pending"
                      inline-prompt
                    />
                    <span class="text-xs text-gray-500">
                      {{
                        nuportFields.auto_approve
                          ? "Auto-approve in Nuport"
                          : "Keep pending for manual approval"
                      }}
                    </span>
                  </div>
                </div>
              </el-form-item>

              <el-form-item>
                <div class="form-elem-container">
                  <FormLabel>Delivery Charge</FormLabel>
                  <div class="flex items-center gap-3 h-8">
                    <el-checkbox v-model="nuportFields.free_delivery">
                      Free delivery
                    </el-checkbox>
                    <span class="text-xs text-gray-500">
                      {{
                        nuportFields.free_delivery
                          ? "Sends ৳0 delivery charge to Nuport"
                          : "Sends the entered delivery charge to Nuport"
                      }}
                    </span>
                  </div>
                </div>
              </el-form-item>
            </div>

            <!-- BizMotion -->
            <div
              v-if="
                selectedDeliveryIntegration &&
                selectedDeliveryIntegrationPartner === 'bizmotion'
              "
              class="mt-2"
            >
              <div
                v-if="!bizmotionCanCreateOrder"
                class="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3"
              >
                BizMotion has not allowed this account to create orders. Tick
                "Allow the API to create orders" on Settings &rarr; API, then
                re-sync the integration.
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <el-form-item>
                  <div class="form-elem-container">
                    <FormLabel>Order Status</FormLabel>
                    <el-select
                      v-model="bizmotionFields.bizmotion_status"
                      placeholder="Pending (default)"
                      filterable
                      clearable
                    >
                      <el-option
                        v-for="status in bizmotionCreateStatuses"
                        :key="status"
                        :label="status"
                        :value="status"
                      />
                    </el-select>
                  </div>
                </el-form-item>

                <el-form-item>
                  <div class="form-elem-container">
                    <FormLabel>Order Source</FormLabel>
                    <el-select
                      v-model="bizmotionFields.bizmotion_source"
                      :placeholder="bizmotionDefaultSource"
                      filterable
                      clearable
                    >
                      <el-option
                        v-for="source in bizmotionSources"
                        :key="source"
                        :label="source"
                        :value="source"
                      />
                    </el-select>
                  </div>
                </el-form-item>

                <el-form-item>
                  <div class="form-elem-container">
                    <FormLabel>District</FormLabel>
                    <el-input
                      v-model="bizmotionFields.bizmotion_district"
                      placeholder="Guessed from the address if left empty"
                    />
                  </div>
                </el-form-item>

                <el-form-item>
                  <div class="form-elem-container">
                    <FormLabel>City</FormLabel>
                    <el-input
                      v-model="bizmotionFields.bizmotion_city"
                      placeholder="Optional"
                    />
                  </div>
                </el-form-item>

                <el-form-item>
                  <div class="form-elem-container">
                    <FormLabel>Area</FormLabel>
                    <el-input
                      v-model="bizmotionFields.bizmotion_area"
                      placeholder="Optional"
                    />
                  </div>
                </el-form-item>

                <el-form-item>
                  <div class="form-elem-container">
                    <FormLabel
                      :required="parseFloat(formData.advance || '0') > 0"
                    >
                      Advance Payment Method
                    </FormLabel>
                    <el-select
                      v-model="bizmotionFields.paymentMethod"
                      placeholder="Select payment method"
                    >
                      <el-option
                        label="Cash on Delivery"
                        value="Cash on Delivery"
                      />
                      <el-option label="Cash" value="Cash" />
                      <el-option label="bKash" value="bKash" />
                      <el-option label="Nagad" value="Nagad" />
                      <el-option label="Rocket" value="Rocket" />
                      <el-option label="Upay" value="Upay" />
                      <el-option label="Card" value="Card" />
                      <el-option label="Bank Transfer" value="Bank Transfer" />
                    </el-select>
                  </div>
                </el-form-item>

                <el-form-item>
                  <div class="form-elem-container">
                    <FormLabel>Delivery Charge</FormLabel>
                    <div class="flex items-center gap-3 h-8">
                      <el-checkbox v-model="bizmotionFields.free_delivery">
                        Free delivery
                      </el-checkbox>
                      <span class="text-xs text-gray-500">
                        {{
                          bizmotionFields.free_delivery
                            ? "Sends ৳0 shipping charge to BizMotion"
                            : "Sends the entered delivery charge to BizMotion"
                        }}
                      </span>
                    </div>
                  </div>
                </el-form-item>

                <el-form-item>
                  <div class="form-elem-container">
                    <FormLabel>Repeat Orders</FormLabel>
                    <div class="flex items-center gap-3 h-8">
                      <el-checkbox v-model="bizmotionFields.reject_duplicate">
                        Refuse duplicates
                      </el-checkbox>
                      <span class="text-xs text-gray-500">
                        {{
                          bizmotionFields.reject_duplicate
                            ? "A repeat order from this customer is refused"
                            : "A repeat order is created and flagged"
                        }}
                      </span>
                    </div>
                  </div>
                </el-form-item>
              </div>
            </div>

            <!-- Address -->
          </div>
        </div>

        <!-- Multiple product rows -->
        <!-- Header row -->
        <div class="flex flex-col">
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div
              class="order-1 lg:order-2 border rounded-lg p-2 sm:p-4 lg:col-span-4 bg-[#d7d7e7]/[.5] border border-gray-400"
            >
              <div class="flex items-center justify-between gap-2 mb-3">
                <h3 class="text-base sm:text-lg font-semibold">
                  Click To Add Products
                </h3>
                <el-button
                  v-if="canCreateProduct"
                  size="small"
                  plain
                  class="shrink-0"
                  @click.prevent="productStore.setCreateModalVisibility(true)"
                >
                  <Svgs
                    name="plus"
                    class="w-4 h-4 mr-1 text-light-sec-purple dark:text-white"
                    stroke-width="1.2"
                  />
                  Create Product
                </el-button>
              </div>

              <!-- Search Inputs -->
              <div class="mb-3 grid gap-2">
                <el-input
                  v-model="productSearchQuery"
                  placeholder="Search by Name or SKU"
                  size="small"
                  clearable
                />
                <el-input
                  v-model="productColorQuery"
                  placeholder="Search by Color"
                  size="small"
                  clearable
                />
                <el-input
                  v-model="productSizeQuery"
                  placeholder="Search by Size"
                  size="small"
                  clearable
                />
              </div>

              <!-- Product List -->
              <div
                class="max-h-[600px] overflow-y-auto space-y-2 grid gap-2 grid-cols-1"
              >
                <template
                  v-for="group in groupedProductsForSelector"
                  :key="group.key"
                >
                  <!-- Parent product header (only for grouped variants) -->
                  <div
                    v-if="group.isGroup"
                    class="text-xs font-semibold text-gray-700 dark:text-light-font px-1 pt-1"
                  >
                    {{ group.name }}
                    <span class="text-gray-400 font-normal"
                      >({{ group.products.length }})</span
                    >
                  </div>
                  <div
                    v-for="product in group.products"
                    :key="product.id"
                    :class="[
                      'border rounded-lg p-2 sm:p-3 transition-colors',
                      group.isGroup ? 'ml-2' : '',
                      isProductDisabled(product)
                        ? 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60 dark:bg-dark-feature dark:border-dark-primary-border'
                        : 'border-gray-400 bg-white cursor-pointer hover:bg-gray-50 dark:bg-dark-blue dark:text-light-font dark:border-dark-primary-border dark:hover:bg-dark-tag',
                    ]"
                    @click="selectProduct(product.id)"
                  >
                    <div class="flex items-start gap-2 sm:gap-3">
                      <!-- Product Image -->
                      <img
                        v-if="product.thumbnail_public_url"
                        :src="product.thumbnail_public_url"
                        :alt="product.name"
                        class="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded border flex-shrink-0"
                      />
                      <div
                        v-else
                        class="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 dark:bg-dark-tag dark:border-dark-primary-border rounded border flex items-center justify-center flex-shrink-0"
                      >
                        <span class="text-xs text-gray-400">No image</span>
                      </div>

                      <!-- Product Info -->
                      <div class="flex-1 min-w-0">
                        <div class="flex justify-between items-start">
                          <div class="flex-1 min-w-0">
                            <h4
                              class="font-medium text-xs sm:text-sm break-words"
                            >
                              {{ product.name }}
                              <br />

                              <span class="text-[11px]"
                                >(Color: {{ product.color }}, Size:
                                {{ product.size }})</span
                              >
                            </h4>
                            <p class="text-xs text-teal-600 truncate">
                              SKU: {{ product.sku || "0" }}
                            </p>
                            <!--
                              Said out loud, because the number comes from
                              somewhere else and can change while the agent is
                              still on the phone. Showing it as if it were our
                              own stock is how somebody promises what is gone.
                            -->
                            <p
                              v-if="isStoreProduct(product)"
                              class="text-xs text-gray-500 mt-0.5"
                            >
                              <el-tag size="small" type="info" class="mr-1">
                                Shopify
                              </el-tag>
                              <span v-if="storeStock(product) !== null">
                                {{ storeStock(product) }} at the shop
                              </span>
                              <span v-else>
                                shop not linked to a place yet
                              </span>
                            </p>
                            <el-tag
                              v-if="isOutOfStock(product)"
                              size="small"
                              type="danger"
                              effect="dark"
                              class="mt-1"
                            >
                              No stock
                            </el-tag>
                          </div>
                        </div>
                        <div class="flex justify-between items-center mt-1">
                          <!-- With a sale price the regular price is struck
                               through, since the sale price is what the order
                               is created at. -->
                          <span
                            v-if="productSalePrice(product)"
                            class="text-xs flex items-center gap-1.5"
                          >
                            <span class="text-gray-400 line-through"
                              >৳{{ product.price || 0 }}</span
                            >
                            <span class="text-green-600 font-medium"
                              >Sale: ৳{{ productSalePrice(product) }}</span
                            >
                          </span>
                          <span v-else class="text-xs"
                            >Price: ৳{{ product.price || 0 }}</span
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <div
                  v-if="filteredProductsForSelector.length === 0"
                  class="text-center text-gray-400 py-4"
                >
                  No products found
                </div>
              </div>
            </div>
            <div
              class="order-2 lg:order-1 border border-gray-400 rounded-lg p-2 sm:p-4 lg:col-span-8"
            >
              <h3 class="text-lg font-semibold mb-3">Ordered Products</h3>

              <div
                v-if="
                  formData.products.filter((p) => p.product_id).length === 0
                "
                class="text-red-500 text-sm"
              >
                No Products added. Please add products to the order
              </div>

              <div v-else class="space-y-2 max-h-[560px] overflow-y-auto">
                <div
                  v-for="(item, index) in formData.products"
                  :key="index"
                  class="border border-gray-400 rounded-lg p-3"
                >
                  <template v-if="item.product_id">
                    <div class="flex items-start gap-3">
                      <!-- Product Image -->
                      <img
                        v-if="
                          getSelectedProduct(item.product_id)
                            ?.thumbnail_public_url
                        "
                        :src="
                          getSelectedProduct(item.product_id)
                            ?.thumbnail_public_url
                        "
                        :alt="getSelectedProduct(item.product_id)?.name"
                        class="w-20 h-20 object-cover rounded border"
                      />
                      <div
                        v-else
                        class="w-20 h-20 bg-gray-100 dark:bg-dark-tag dark:border-dark-primary-border rounded border flex items-center justify-center"
                      >
                        <span class="text-xs text-gray-400">No image</span>
                      </div>

                      <!-- Product Details -->
                      <div class="flex-1">
                        <div class="flex justify-between items-start mb-2">
                          <div>
                            <h4 class="font-medium text-sm">
                              {{ getSelectedProduct(item.product_id)?.name }}
                              <span class="text-[11px]"
                                >(Color:
                                {{
                                  getSelectedProduct(item.product_id)?.color
                                }}, Size:
                                {{
                                  getSelectedProduct(item.product_id)?.size
                                }})</span
                              >
                            </h4>
                            <p class="text-xs text-teal-600">
                              SKU:
                              {{ getSelectedProduct(item.product_id)?.sku }}
                            </p>
                          </div>
                          <el-icon
                            class="text-gray-400 cursor-pointer hover:text-red-500"
                            @click="removeSelectedProduct(index)"
                          >
                            <Close />
                          </el-icon>
                        </div>

                        <div
                          :class="[
                            'grid gap-2',
                            isEcomDriveSelected
                              ? 'grid-cols-2 sm:grid-cols-3'
                              : 'grid-cols-2 sm:grid-cols-5',
                          ]"
                        >
                          <div>
                            <label class="text-xs text-gray-500"
                              >Unit Prices</label
                            >
                            <el-input
                              v-model.number="item.unit_price"
                              type="number"
                              size="small"
                              @wheel="(e) => e.preventDefault()"
                              @input="item.price = calculateProductTotal(item)"
                            />
                          </div>
                          <div>
                            <label class="text-xs text-gray-500">Unit</label>
                            <el-input
                              v-model.number="item.quantity"
                              type="number"
                              size="small"
                              :min="1"
                              @wheel="(e) => e.preventDefault()"
                              @input="item.price = calculateProductTotal(item)"
                            />
                          </div>
                          <div v-if="!isEcomDriveSelected">
                            <label class="text-xs text-gray-500"
                              >Discount Type</label
                            >
                            <el-select
                              v-model="item.discount_type"
                              size="small"
                              class="w-full"
                              @change="item.price = calculateProductTotal(item)"
                            >
                              <el-option label="Fixed" :value="1" />
                              <el-option label="Percent" :value="2" />
                            </el-select>
                          </div>
                          <div v-if="!isEcomDriveSelected">
                            <label class="text-xs text-gray-500"
                              >Discount</label
                            >
                            <el-input
                              v-model.number="item.discount_amount"
                              type="number"
                              size="small"
                              :min="0"
                              @wheel="(e) => e.preventDefault()"
                              @input="item.price = calculateProductTotal(item)"
                            />
                          </div>
                          <div>
                            <label class="text-xs text-gray-500"
                              >Total Amount</label
                            >
                            <div
                              class="text-sm font-semibold mt-1 text-teal-600"
                            >
                              ৳{{ calculateProductTotal(item).toFixed(2) }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- Left: Ordered Products -->

            <!-- Right: Click To Add Products -->
          </div>
        </div>

        <div class="grid grid-cols-4 gap-2">
          <div class="flex flex-col gap-1">
            <FormLabel :required="hasDeliveryIntegrations"
              >Delivery Charge</FormLabel
            >
            <el-form-item prop="deliveryCharge" :rules="deliveryChargeRules">
              <el-input
                v-model="formData.deliveryCharge"
                type="number"
                placeholder="0"
                :disabled="
                  (selectedDeliveryIntegrationPartner === 'nuport' &&
                    nuportFields.free_delivery) ||
                  (selectedDeliveryIntegrationPartner === 'bizmotion' &&
                    bizmotionFields.free_delivery)
                "
                @wheel="(e) => e.preventDefault()"
              />
            </el-form-item>
          </div>
          <div class="flex flex-col gap-1">
            <FormLabel>Discount</FormLabel>
            <el-form-item prop="discount">
              <el-input
                v-model="formData.discount"
                type="number"
                placeholder="0"
                @wheel="(e) => e.preventDefault()"
              />
            </el-form-item>
          </div>
          <div v-if="!isEcomDriveSelected" class="flex flex-col gap-1">
            <FormLabel>Advance</FormLabel>
            <el-form-item prop="advance">
              <el-input
                v-model="formData.advance"
                type="number"
                placeholder="0"
                @wheel="(e) => e.preventDefault()"
              />
            </el-form-item>
          </div>
          <div class="flex flex-col gap-1">
            <FormLabel>Sub Total</FormLabel>
            <el-form-item prop="sub_total">
              <el-input
                v-model="formData.sub_total"
                placeholder="Sub Total"
                disabled
              />
            </el-form-item>
          </div>
        </div>

        <div class="grid grid-cols-1">
          <div class="flex flex-col gap-1">
            <FormLabel>Total</FormLabel>
            <el-form-item prop="total">
              <el-input
                v-model="formData.total"
                placeholder="Total amount"
                disabled
              />
            </el-form-item>
          </div>
        </div>

        <el-form-item>
          <FormLabel>Shipping Note</FormLabel>
          <el-input v-model="finalMessage" type="textarea" rows="3" />
        </el-form-item>

        <div
          class="sticky bottom-0 left-0 bg-white border-t py-2 mt-4 flex flex-col gap-2 dark:bg-dark-blue dark:border-dark-primary-border"
          style="z-index: 10"
        >
          <el-alert
            v-if="saveError"
            type="error"
            show-icon
            :title="saveError"
            @close="saveError = ''"
          />
          <div class="flex justify-end gap-2">
            <NavButton
              v-if="!formSubmitting"
              @click="sendOrderConfirmation(true)"
            >
              <span
                >{{ props.isEditMode ? "Update" : "Create" }}
                {{ showStatusSelect ? "Order" : "Draft Order" }}</span
              >
            </NavButton>
            <NavButton v-else> Processing </NavButton>
            <!-- <NavButton
            class="bg-light-purple-nav"
            @click="sendOrderConfirmation()"
          >
            Send
          </NavButton> -->
          </div>
        </div>
      </el-form>
    </div>

    <!-- Create a product without leaving the order. Bound to the store's own
         visibility flag so the form's Cancel and Create both close it. -->
    <el-dialog
      v-model="productStore.showCreateModal"
      :lock-scroll="false"
      :modal="false"
      :show-close="true"
      :destroy-on-close="true"
      class="rounded-lg"
      draggable
      align-center
      width="35%"
      @close="productStore.setCreateModalVisibility(false)"
    >
      <template #header="{ titleId, titleClass }">
        <div class="my-header flex flex-col gap-1">
          <h4 :id="titleId" class="text-md" :class="titleClass">
            Create Product
          </h4>
        </div>
      </template>
      <div class="subtext-dialog">
        <ProductsCreateForm
          :show-edit-modal-header="false"
          @created="onProductCreated"
        />
      </div>
    </el-dialog>
  </div>
</template>

<style scoped>
.little_padding {
  padding-top: 1px;
  padding-bottom: 1px;
  padding-right: 11px;
  padding-left: 11px;
}
.width {
  width: 90.04px !important;
}
</style>
